import { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import type { StateHeatmapEntry, OpportunityCluster } from '../../types';
import ClusterTooltip from './ClusterTooltip';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const FIPS_TO_STATE: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY',
};

interface SaturationMapProps {
  stateHeatmap: StateHeatmapEntry[];
  clusters: OpportunityCluster[];
  selectedClusterId: string | null;
  onClusterClick: (id: string) => void;
}

function penetrationColor(pct: number): string {
  if (pct >= 50) return '#22c55e';
  if (pct >= 25) {
    const t = (pct - 25) / 25;
    const r = Math.round(234 + (34 - 234) * t);
    const g = Math.round(179 + (197 - 179) * t);
    const b = Math.round(8 + (94 - 8) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }
  const t = pct / 25;
  const r = Math.round(239 + (234 - 239) * t);
  const g = Math.round(68 + (179 - 68) * t);
  const b = Math.round(8 + (8 - 8) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

function bubbleRadius(totalSchools: number, maxSchools: number): number {
  const minR = 10;
  const maxR = 30;
  return minR + (totalSchools / Math.max(maxSchools, 1)) * (maxR - minR);
}

function scoreColor(score: number): string {
  const t = score / 100;
  const r = Math.round(180 + 75 * t);
  const g = Math.round(120 + 20 * t - 60 * t);
  const b = Math.round(60 - 40 * t);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function SaturationMap({ stateHeatmap, clusters, selectedClusterId, onClusterClick }: SaturationMapProps) {
  const [tooltip, setTooltip] = useState<{ cluster: OpportunityCluster; x: number; y: number } | null>(null);

  const stateMap = useMemo(() => {
    const m = new Map<string, StateHeatmapEntry>();
    for (const s of stateHeatmap) m.set(s.stateCode, s);
    return m;
  }, [stateHeatmap]);

  const maxSchools = useMemo(() => Math.max(...clusters.map(c => c.totalSchools), 1), [clusters]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm relative">
      <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 800 }}
        width={800} height={500} style={{ width: '100%', height: 'auto' }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateCode = FIPS_TO_STATE[geo.id];
              const entry = stateCode ? stateMap.get(stateCode) : undefined;
              const fill = entry ? penetrationColor(entry.penetrationPct) : '#e5e7eb';
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="#fff"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none', opacity: 0.85 },
                    hover: { outline: 'none', opacity: 1 },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>

        {clusters.map((cluster) => {
          const r = bubbleRadius(cluster.totalSchools, maxSchools);
          const isSelected = cluster.id === selectedClusterId;
          const isTop = cluster.rank === 1;
          return (
            <Marker
              key={cluster.id}
              coordinates={[cluster.centerLng, cluster.centerLat]}
              onMouseEnter={(e) => setTooltip({ cluster, x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setTooltip(null)}
              onClick={() => onClusterClick(cluster.id)}
              style={{ cursor: 'pointer' }}
            >
              {isTop && (
                <circle r={r + 4} fill={scoreColor(cluster.opportunityScore)} opacity={0.3}>
                  <animate attributeName="r" values={`${r + 2};${r + 8};${r + 2}`} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
              <circle
                r={r}
                fill={scoreColor(cluster.opportunityScore)}
                stroke={isSelected ? '#3b82f6' : '#fff'}
                strokeWidth={isSelected ? 3 : 1.5}
                opacity={0.9}
                filter="drop-shadow(0 1px 2px rgba(0,0,0,0.3))"
              />
              <text textAnchor="middle" dy={4} fontSize={r * 0.7} fill="#fff" fontWeight="bold" pointerEvents="none">
                {cluster.rank}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>

      <div className="flex items-center justify-center gap-2 pb-3">
        <span className="text-xs text-gray-500 dark:text-gray-400">0% Blast</span>
        <div className="h-2 w-40 rounded-full" style={{
          background: 'linear-gradient(to right, #ef4444, #eab308, #22c55e)',
        }} />
        <span className="text-xs text-gray-500 dark:text-gray-400">50%+ Blast</span>
      </div>

      {tooltip && <ClusterTooltip cluster={tooltip.cluster} x={tooltip.x} y={tooltip.y} />}
    </div>
  );
}
